import type { FeedItemDto } from './wire';

/**
 * The feed's client-side model: the API row with dates parsed and the derived
 * facts every view needs (region, progress, eligibility) computed once.
 */
export interface Deal extends Omit<
  FeedItemDto,
  'launchedAt' | 'deadlineAt' | 'firstSeenAt' | 'checkedAt' | 'perkEndsAt'
> {
  launchedAt: Date | null;
  deadlineAt: Date | null;
  firstSeenAt: Date;
  checkedAt: Date;
  perkEndsAt: Date | null;
  /** What the platform chip and the platform filter say. */
  platformLabel: string;
  region: Region;
  /** Issuer country, falling back to where the platform is. */
  country: string | null;
  /** raised / max, when both are known. */
  pctOfMax: number | null;
  /** raised / target, when both are known. */
  pctOfTarget: number | null;
  nearCap: boolean;
  /** Very rough USD figures, for ordering and the ticket slider only. */
  minTicketUsd: number | null;
  raisedUsd: number | null;
}

/** The brief's markets (§5.1 coverage strip), shared by the feed and the directory. */
export type Region =
  'US' | 'UK' | 'EU' | 'AU/NZ' | 'CA' | 'Asia' | 'LatAm' | 'Other';
export const REGIONS: Region[] = [
  'US',
  'UK',
  'EU',
  'AU/NZ',
  'CA',
  'Asia',
  'LatAm',
  'Other',
];

/**
 * The Status options in lifecycle order, with what each reads. The feed
 * carries no others: closed and withdrawn raises never reach it. OPEN reads
 * "Live", as the badge does on a raise with nothing more pressing to say.
 */
export const STATUSES: [Deal['status'], string][] = [
  ['UPCOMING', 'Upcoming'],
  ['OPEN', 'Live'],
];

const EU = new Set(
  'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(
    ' ',
  ),
);
const ASIA = new Set('CN HK ID IN JP KR MY PH SG TH TW VN'.split(' '));
const LATAM = new Set('AR BO BR CL CO CR EC MX PE PY UY'.split(' '));

export function regionOf(country: string | null): Region {
  if (!country) return 'Other';
  if (country === 'US') return 'US';
  if (country === 'GB') return 'UK';
  if (EU.has(country)) return 'EU';
  if (country === 'AU' || country === 'NZ') return 'AU/NZ';
  if (country === 'CA') return 'CA';
  if (ASIA.has(country)) return 'Asia';
  if (LATAM.has(country)) return 'LatAm';
  return 'Other';
}

/**
 * The region a raise is filed under: its issuer's country, falling back to
 * where the platform is. What the feed's Region filter, a rule's regions and
 * /get-started's region counts all go by.
 */
export function regionOfItem(item: {
  issuerCountry: string | null;
  platformCountry: string | null;
}): Region {
  return regionOf(item.issuerCountry ?? item.platformCountry);
}

/**
 * Approximate USD rates, deliberately coarse and not for display: they only
 * decide which of two raises in different currencies is "bigger" and where a
 * minimum ticket falls on the slider. Being 5% off moves nothing that matters.
 * Exports print the day's rates instead (src/export/fx-rates.ts), and these
 * only when those cannot be fetched, saying so.
 */
export const APPROX_USD: Record<string, number> = {
  USD: 1,
  GBP: 1.3,
  EUR: 1.1,
  CAD: 0.73,
  AUD: 0.65,
  NZD: 0.6,
  PLN: 0.27,
  MYR: 0.24,
  BRL: 0.18,
};

/**
 * A currency with no rate above is left unranked rather than read as dollars:
 * taken 1:1, Emiteo's PLN 666,159 would outrank a $500,000 raise.
 */
function toUsd(amount: number | null, currency: string | null): number | null {
  const rate = APPROX_USD[currency ?? 'USD'];
  if (amount === null || rate === undefined) return null;
  return amount * rate;
}

const date = (value: string | null) => (value ? new Date(value) : null);

/** The share of its maximum at which a raise is near its cap: the feed's rail, /raises/near-cap. */
export const NEAR_CAP_PCT = 90;

/** How much of its maximum a raise has raised; null with no amount reported or no maximum stated. */
export function pctOfMaximum(
  raised: number | null,
  max: number | null,
): number | null {
  return raised !== null && max ? (raised / max) * 100 : null;
}

export function toDeal(item: FeedItemDto): Deal {
  const pctOfMax = pctOfMaximum(item.raisedAmount, item.maxAmount);
  const pctOfTarget =
    item.raisedAmount !== null && item.targetAmount
      ? (item.raisedAmount / item.targetAmount) * 100
      : null;
  const country = item.issuerCountry ?? item.platformCountry;
  return {
    ...item,
    launchedAt: date(item.launchedAt),
    deadlineAt: date(item.deadlineAt),
    firstSeenAt: new Date(item.firstSeenAt),
    checkedAt: new Date(item.checkedAt),
    perkEndsAt: date(item.perkEndsAt),
    platformLabel: item.platformName ?? item.sourceName,
    region: regionOfItem(item),
    country,
    pctOfMax,
    pctOfTarget,
    // Reservations do not fill an offering, so an upcoming raise is never at
    // its cap, however much has been reserved against it.
    nearCap:
      item.status !== 'UPCOMING' &&
      pctOfMax !== null &&
      pctOfMax >= NEAR_CAP_PCT,
    minTicketUsd: toUsd(item.minTicket, item.currency),
    raisedUsd: toUsd(item.raisedAmount, item.currency),
  };
}

/**
 * Whether a viewer in `country` may invest. Only a platform that restricts to
 * its own residents can say no; "not stated" is not a no (brief §7: badge or
 * grey out, never hide on a guess).
 */
export function eligibleFrom(
  deal: Pick<Deal, 'openToForeign' | 'platformCountry'>,
  country: string | null,
): boolean {
  if (deal.openToForeign !== false) return true;
  return country !== null && country === deal.platformCountry;
}

/** The "Sector" option for raises with no sector listed. */
export const OTHER_SECTOR = 'other';

/** A sector's spelling-blind key. Mirrors sectorKey in src/feed/feed.rules.ts. */
export function sectorKey(sector: string): string {
  return sector.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * The "Sector" options a deal falls under: each sector's spelling-blind key,
 * so "Clean Tech" and "Cleantech" are one option, or "Other" when it lists
 * none. Mirrors sectorFacet in src/feed/feed.rules.ts.
 */
export function sectorKeys(sectors: string[]): string[] {
  const keys = sectors.map(sectorKey).filter(Boolean);
  return keys.length > 0 ? keys : [OTHER_SECTOR];
}

/**
 * Whether the deal's link is its SEC filing rather than a campaign page. An
 * EDGAR row's always is; a DealMaker row's is the issuer's own offering page
 * wherever the adapter found one, and its Form 1-A only where it did not. So
 * this asks the link, not the source.
 */
export function isFiling(deal: Pick<Deal, 'url'>): boolean {
  try {
    return /(^|\.)sec\.gov$/.test(new URL(deal.url).hostname);
  } catch {
    return false;
  }
}

/**
 * What the deal's amount counts. An upcoming raise takes no money yet, so a
 * figure on it is non-binding reservations — a Wefunder testing-the-waters
 * page's "$61,181 reserved by 85 investors" — not money raised.
 */
export function amountVerb(deal: Pick<Deal, 'status'>): 'raised' | 'reserved' {
  return deal.status === 'UPCOMING' ? 'reserved' : 'raised';
}

const DAY = 86_400_000;

/**
 * "New" means new to the world, not new to us: launch (or Form C filing) in
 * the last 48 hours. A deal whose source gives no launch date is never
 * badged, rather than every such deal being "new" the day we start tracking.
 */
export function isNew(deal: Deal, now: number, windowMs = 2 * DAY): boolean {
  return (
    deal.launchedAt !== null && now - deal.launchedAt.getTime() <= windowMs
  );
}

export function closesWithin(deal: Deal, now: number, ms: number): boolean {
  if (!deal.deadlineAt) return false;
  const left = deal.deadlineAt.getTime() - now;
  return left > 0 && left <= ms;
}

// ---- Filters ---------------------------------------------------------------

export type QuickRail = 'new' | 'week' | 'cap';
export type Sort = 'closing' | 'newest' | 'raised' | 'funded';

/** The quick rails as the top bar reads them; null is "All". */
export const QUICK_RAILS: [QuickRail | null, string][] = [
  [null, 'All'],
  ['new', 'New this week'],
  ['week', 'Closing this week'],
  ['cap', 'Near cap'],
];

/**
 * The quick rail an address asks for, as the closing-soon alert's "+n more"
 * link does with `?rail=week`; null for none, or one that is not a rail.
 */
export function railOf(search: string): QuickRail | null {
  const asked = new URLSearchParams(search).get('rail');
  return (
    QUICK_RAILS.find(([rail]) => rail !== null && rail === asked)?.[0] ?? null
  );
}

/** The ticket slider's figure: "$250", "$2.5k". */
export const ticketUsd = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toLocaleString('en-GB')}k` : `$${n}`;

/** The sort options as the Sort control reads them. */
export const SORTS: [Sort, string][] = [
  ['closing', 'Closing soonest'],
  ['newest', 'Newest'],
  ['raised', 'Most raised'],
  ['funded', 'Most funded (%)'],
];

export interface Filters {
  openToMe: boolean;
  hasPerk: boolean;
  /** Slider value in USD; null means "any". */
  maxTicketUsd: number | null;
  /** Campaign statuses as the API sends them ('OPEN'), not as they read. */
  statuses: string[];
  regions: string[];
  /** Keys from GET /api/feed/sectors, not display names. */
  sectors: string[];
  countries: string[];
  platforms: string[];
  regimes: string[];
  securities: string[];
  quick: QuickRail | null;
  search: string;
}

export const NO_FILTERS: Filters = {
  openToMe: false,
  hasPerk: false,
  maxTicketUsd: null,
  statuses: [],
  regions: [],
  sectors: [],
  countries: [],
  platforms: [],
  regimes: [],
  securities: [],
  quick: null,
  search: '',
};

/**
 * The filters as a plan without every filter applies them (ALL_FILTERS):
 * status and region, with the search and the quick rails beside them. The
 * rest are reset, so a filter set before a plan ended cannot go on narrowing
 * a feed whose rail no longer shows it. The API counts with the same ones.
 */
export function freeFilters(filters: Filters): Filters {
  return {
    ...NO_FILTERS,
    statuses: filters.statuses,
    regions: filters.regions,
    quick: filters.quick,
    search: filters.search,
  };
}

export function activeFilterCount(f: Filters): number {
  return (
    Number(f.openToMe) +
    Number(f.hasPerk) +
    Number(f.maxTicketUsd !== null) +
    f.statuses.length +
    f.regions.length +
    f.sectors.length +
    f.countries.length +
    f.platforms.length +
    f.regimes.length +
    f.securities.length +
    Number(f.quick !== null) +
    Number(f.search.trim() !== '')
  );
}

export function matchesQuick(deal: Deal, rail: QuickRail, now: number) {
  if (rail === 'new') return isNew(deal, now, 7 * DAY);
  if (rail === 'week') return closesWithin(deal, now, 7 * DAY);
  return deal.nearCap;
}

export function applyFilters(
  deals: Deal[],
  f: Filters,
  viewerCountry: string | null,
  now: number,
): Deal[] {
  const q = f.search.trim().toLowerCase();
  const any = (list: string[], value: string | null) =>
    list.length === 0 || (value !== null && list.includes(value));

  return deals.filter(
    (d) =>
      (!f.openToMe || eligibleFrom(d, viewerCountry)) &&
      (!f.hasPerk || d.perk !== null) &&
      // Unknown minimum tickets stay in: absence of data is not a high ticket.
      (f.maxTicketUsd === null ||
        d.minTicketUsd === null ||
        d.minTicketUsd <= f.maxTicketUsd) &&
      any(f.statuses, d.status) &&
      any(f.regions, d.region) &&
      (f.sectors.length === 0 ||
        sectorKeys(d.sectors).some((k) => f.sectors.includes(k))) &&
      any(f.countries, d.country) &&
      any(f.platforms, d.platformLabel) &&
      any(f.regimes, d.regime) &&
      bySecurity(d, f.securities) &&
      (f.quick === null || matchesQuick(d, f.quick, now)) &&
      (q === '' ||
        [d.issuerName, d.tagline, d.platformLabel, ...d.sectors]
          .filter(Boolean)
          .some((text) => text!.toLowerCase().includes(q))),
  );
}

/**
 * The Security type filter. With nothing ticked it passes the raises in
 * scope, so a loan never shows unasked; ticked classes pass whatever they
 * name, "Bond / loan" included. Mirrors inScope in src/feed/feed.rules.ts.
 */
function bySecurity(deal: Deal, selected: string[]): boolean {
  if (selected.length === 0) return deal.inScope;
  return deal.securityClass !== null && selected.includes(deal.securityClass);
}

/**
 * Option → count, sorted by count then name, for one filter facet. Every live
 * raise names options, but only those `counted` add to them: by default the
 * raises in scope, so the numbers describe the feed as it first opens, and a
 * platform that lists only loans is an option at 0 rather than missing.
 */
export function facet(
  deals: Deal[],
  key: (d: Deal) => string | null,
  counted: (d: Deal) => boolean = (d) => d.inScope,
): [string, number][] {
  const counts = new Map<string, number>();
  for (const d of deals) {
    const value = key(d);
    if (value) {
      counts.set(value, (counts.get(value) ?? 0) + Number(counted(d)));
    }
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

export function sortDeals(deals: Deal[], sort: Sort): Deal[] {
  const time = (d: Date | null, fallback: number) => d?.getTime() ?? fallback;
  const sorted = [...deals];
  switch (sort) {
    case 'closing':
      // No stated deadline sorts last, not first.
      sorted.sort(
        (a, b) => time(a.deadlineAt, Infinity) - time(b.deadlineAt, Infinity),
      );
      break;
    case 'newest':
      sorted.sort(
        (a, b) =>
          time(b.launchedAt, b.firstSeenAt.getTime()) -
          time(a.launchedAt, a.firstSeenAt.getTime()),
      );
      break;
    case 'raised':
      sorted.sort((a, b) => (b.raisedUsd ?? -1) - (a.raisedUsd ?? -1));
      break;
    case 'funded':
      sorted.sort(
        (a, b) =>
          (b.pctOfTarget ?? b.pctOfMax ?? -1) -
          (a.pctOfTarget ?? a.pctOfMax ?? -1),
      );
      break;
  }
  return sorted;
}
